/*
 * LEDBAZIS – product imagery presentation (purely visual; no data or media is changed).
 *
 * V8: product photos (gallery, thumbnails, product cards, accessory/related cards, sticky bar, cart) are shown
 * on a white #FFFFFF stage. They are NOT cut out any more – the "fit" mode below only measures where the
 * product sits inside its (often very wide) white canvas, from a tiny 240px copy, and scales the <img>
 * (object-fit: contain + transform) so the PRODUCT fills its frame with an even white margin. Only white
 * canvas is ever outside the frame; the product itself is never cropped, distorted or recoloured.
 * The original cut-out pipeline below is kept only for the product-page feature section.
 *
 * OSRAM pack shots are delivered on pure white. On the dark storefront we lift the product off its
 * white backdrop at display time:
 *   1) a small CORS copy of the image is drawn to a canvas;
 *   2) the backdrop is found by flood-filling from the image border across SMOOTH, NEUTRAL, LIGHT pixels
 *      only (a "magic wand" with local tolerance) – object outlines stop the fill, so white packaging,
 *      glass and chrome inside the product are never touched and no colours are inverted;
 *   3) backdrop pixels become transparent (soft shadows become a soft dark shadow), the 1px fringe is
 *      de-whitened, and the result is used as the <img> source (blob URL);
 *   4) the visible product bounding box is measured so the PRODUCT (not the file) fills its frame
 *      with an even margin (--lb-z / --lb-dx / --lb-dy). Nothing is ever cropped or distorted.
 * Photos that are not on a white backdrop (studio, lifestyle) are left exactly as they are.
 */
(() => {
  const root = document.documentElement;
  if (!('createImageBitmap' in window) || !window.fetch || !window.URL) {
    root.classList.remove('lb-js');
    return;
  }

  const TARGETS = [
    /* white photo stage – measure + fit only (zmin < 1: products that touch the file edge get the same margin) */
    { sel: '.product-information__media .product-media-container img', w: 240, pad: 0.09, zmax: 2.4, zmin: 0.84, fit: true },
    { sel: '.product-information__media .slideshow-controls__thumbnails .slideshow-control img', w: 160, pad: 0.08, zmax: 2.6, zmin: 0.84, fit: true },
    { sel: '.product-card .card-gallery img', w: 240, pad: 0.1, zmax: 2.6, zmin: 0.84, fit: true },
    { sel: '.lb-card__media img', w: 240, pad: 0.1, zmax: 2.6, zmin: 0.84, fit: true },
    { sel: '.sticky-add-to-cart__image img, .lb-acc__media img, .cart-items__media img', w: 160, pad: 0.08, zmax: 2.6, zmin: 0.84, fit: true },
    /* decorative dark section on the product page – cut-out + fit
       (V9: the homepage category tiles and the footer CTA card use transparent PNGs and are NOT processed) */
    { sel: '.lb-feature__media img', w: 1200, pad: 0.1, zmax: 1.6 }
  ];
  const SKIP = 'deferred-media, product-model, model-viewer, .product-media-container--model, .product-media-container--video, .product-media-container--external_video';

  const lum = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const cache = new Map();

  const sized = (u, w) => {
    const x = new URL(u, location.href);
    x.searchParams.set('width', String(w));
    x.searchParams.delete('height');
    x.searchParams.delete('crop');
    return x.href;
  };

  async function loadBitmap(u, w) {
    const res = await fetch(sized(u, w), { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error('img ' + res.status);
    return createImageBitmap(await res.blob());
  }

  /* ---------- backdrop removal + measurement ---------- */
  async function analyse(src, w) {
    const bm = await loadBitmap(src, w);
    const W = bm.width, H = bm.height;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(bm, 0, 0);
    const img = x.getImageData(0, 0, W, H);
    const d = img.data;
    const N = W * H;
    const at = (px, py) => (py * W + px) * 4;

    const corners = [at(2, 2), at(W - 3, 2), at(2, H - 3), at(W - 3, H - 3)];
    const transparentCorners = corners.filter((k) => d[k + 3] < 16).length >= 3;
    const whiteCorners = corners.every((k) => {
      const r = d[k], g = d[k + 1], b = d[k + 2];
      return d[k + 3] > 240 && Math.max(r, g, b) - Math.min(r, g, b) < 14 && lum(r, g, b) > 232;
    });

    let cut = false;
    if (whiteCorners) {
      const L = new Float32Array(N);
      const C = new Uint8Array(N);
      for (let i = 0; i < N; i++) {
        const k = i * 4, r = d[k], g = d[k + 1], b = d[k + 2];
        L[i] = lum(r, g, b);
        C[i] = Math.max(r, g, b) - Math.min(r, g, b);
      }
      const filled = new Uint8Array(N);
      const q = new Int32Array(N);
      let head = 0, tail = 0;
      // pass 1 – strict: only (near) pure white, very smooth → keeps white box faces, glass and chrome
      const ok1 = (i) => L[i] >= 244 && C[i] <= 10;
      const seed = (i) => {
        if (ok1(i) && !filled[i]) {
          filled[i] = 1;
          q[tail++] = i;
        }
      };
      for (let px = 0; px < W; px++) {
        seed(px);
        seed((H - 1) * W + px);
      }
      for (let py = 0; py < H; py++) {
        seed(py * W);
        seed(py * W + W - 1);
      }
      const grow = (okFn, tol, minRow) => {
        while (head < tail) {
          const i = q[head++];
          const px = i % W, l = L[i];
          const nb = [px > 0 ? i - 1 : -1, px < W - 1 ? i + 1 : -1, i - W, i + W];
          for (const j of nb) {
            if (j < 0 || j >= N || filled[j] || !okFn(j)) continue;
            if (minRow && (j / W) < minRow) continue;
            if (Math.abs(L[j] - l) > tol) continue;
            filled[j] = 1;
            q[tail++] = j;
          }
        }
      };
      grow(ok1, 3, 0);
      // pass 2 – soft cast shadows, only in the lower half of the product (never its top faces)
      let oy0 = H, oy1 = -1;
      for (let i = 0; i < N; i += 3) {
        if (filled[i]) continue;
        const py = (i / W) | 0;
        if (py < oy0) oy0 = py;
        if (py > oy1) oy1 = py;
      }
      if (oy1 > oy0) {
        const yMid = (oy0 + oy1) / 2;
        head = 0;
        tail = 0;
        for (let i = Math.floor(yMid) * W; i < N; i++) if (filled[i]) q[tail++] = i;
        grow((j) => L[j] >= 200 && C[j] <= 14, 6, yMid);
        tail = 0;
        for (let i = 0; i < N; i++) if (filled[i]) tail++;
      }
      const removed = tail / N;
      if (removed > 0.03 && removed < 0.97) {
        cut = true;
        for (let i = 0; i < N; i++) {
          const k = i * 4;
          if (filled[i]) {
            // backdrop → transparent; its soft grey shadows → soft dark shadow
            const a = Math.min(1, ((255 - L[i]) / 255) * 1.6);
            d[k] = d[k + 1] = d[k + 2] = 0;
            d[k + 3] = Math.round(a * 255);
          }
        }
        // de-whiten the 1px fringe of the product edge
        for (let i = 0; i < N; i++) {
          if (filled[i]) continue;
          const px = i % W;
          const edge = (px > 0 && filled[i - 1]) || (px < W - 1 && filled[i + 1]) || (i >= W && filled[i - W]) || (i + W < N && filled[i + W]);
          if (!edge) continue;
          const wht = Math.max(0, Math.min(1, (L[i] - 170) / 85));
          if (wht <= 0) continue;
          const k = i * 4;
          d[k] *= 1 - 0.55 * wht;
          d[k + 1] *= 1 - 0.55 * wht;
          d[k + 2] *= 1 - 0.55 * wht;
          d[k + 3] = Math.round(d[k + 3] * (1 - 0.6 * wht));
        }
        x.putImageData(img, 0, 0);
      }
    }

    // bounding box of the visible product (alpha) – or of non-white content when not cut
    let x0 = W, y0 = H, x1 = -1, y1 = -1;
    const visible = (k) => {
      if (cut || transparentCorners) return d[k + 3] > 60;
      if (!whiteCorners) return true;
      return lum(d[k], d[k + 1], d[k + 2]) < 232;
    };
    const step = W > 900 ? 2 : 1;
    for (let py = 0; py < H; py += step) {
      for (let px = 0; px < W; px += step) {
        if (!visible(at(px, py))) continue;
        if (px < x0) x0 = px;
        if (px > x1) x1 = px;
        if (py < y0) y0 = py;
        if (py > y1) y1 = py;
      }
    }
    const info = x1 < 0 || (!cut && !transparentCorners && !whiteCorners)
      ? null
      : { ar: W / H, x0: x0 / W, y0: y0 / H, x1: Math.min(1, (x1 + step) / W), y1: Math.min(1, (y1 + step) / H) };

    let url = null;
    if (cut) {
      const blob = await new Promise((res) => c.toBlob(res, 'image/webp', 0.92));
      if (blob) url = URL.createObjectURL(blob);
    }
    return { url, info, transparent: transparentCorners };
  }

  /* ---------- measure only (white stage): bounding box of the non-white product ---------- */
  async function measure(src, w) {
    const bm = await loadBitmap(src, w);
    const W = bm.width, H = bm.height;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.fillStyle = '#fff';
    x.fillRect(0, 0, W, H);
    x.drawImage(bm, 0, 0);
    const d = x.getImageData(0, 0, W, H).data;
    const at = (px, py) => (py * W + px) * 4;
    const ink = (k) => lum(d[k], d[k + 1], d[k + 2]) < 238 || Math.max(d[k], d[k + 1], d[k + 2]) - Math.min(d[k], d[k + 1], d[k + 2]) > 16;
    const corners = [at(1, 1), at(W - 2, 1), at(1, H - 2), at(W - 2, H - 2)];
    if (corners.some(ink)) return { info: null }; // not a white-backdrop photo → leave as is
    let x0 = W, y0 = H, x1 = -1, y1 = -1;
    for (let py = 0; py < H; py++) {
      for (let px = 0; px < W; px++) {
        if (!ink(at(px, py))) continue;
        if (px < x0) x0 = px;
        if (px > x1) x1 = px;
        if (py < y0) y0 = py;
        if (py > y1) y1 = py;
      }
    }
    if (x1 < 0) return { info: null };
    return { info: { ar: W / H, x0: x0 / W, y0: y0 / H, x1: Math.min(1, (x1 + 1) / W), y1: Math.min(1, (y1 + 1) / H) } };
  }

  /* ---------- fit ---------- */
  function applyFit(img, info, pad, zmax, zmin = 1) {
    const W = img.clientWidth, H = img.clientHeight;
    if (!info || !W || !H) return;
    const dispW = Math.min(W, H * info.ar);
    const dispH = dispW / info.ar;
    const cw = Math.max(1, (info.x1 - info.x0) * dispW);
    const ch = Math.max(1, (info.y1 - info.y0) * dispH);
    let z = Math.min((W * (1 - 2 * pad)) / cw, (H * (1 - 2 * pad)) / ch);
    z = Math.max(zmin, Math.min(z, zmax));
    if (Math.abs(z - 1) < 0.03) z = 1;
    const cx = ((info.x0 + info.x1) / 2 - 0.5) * dispW;
    const cy = ((info.y0 + info.y1) / 2 - 0.5) * dispH;
    img.style.setProperty('--lb-z', z.toFixed(4));
    img.style.setProperty('--lb-dx', (z === 1 ? 0 : -cx * z).toFixed(1) + 'px');
    img.style.setProperty('--lb-dy', (z === 1 ? 0 : -cy * z).toFixed(1) + 'px');
    img.classList.add('lb-fit');
  }

  /* ---------- pipeline ---------- */
  const state = new WeakMap();
  const ro = new ResizeObserver((entries) => {
    for (const e of entries) {
      const s = state.get(e.target);
      if (s && s.info && !s.cfg.cutOnly) applyFit(e.target, s.info, s.cfg.pad, s.cfg.zmax, s.cfg.zmin);
    }
  });

  let chain = Promise.resolve();
  function enqueue(job) {
    chain = chain.then(job).catch(() => {});
    return chain;
  }

  function reveal(img) {
    img.classList.remove('lb-await');
    img.classList.add('lb-ready');
  }

  function processImg(img, cfg) {
    const s = state.get(img);
    if (!s || s.started) return;
    s.started = true;
    const src = img.currentSrc || img.getAttribute('src');
    if (!src || src.startsWith('data:') || src.startsWith('blob:')) {
      reveal(img);
      return;
    }
    let key;
    try {
      const u = new URL(src, location.href);
      key = u.origin + u.pathname + '|' + cfg.w;
    } catch (e) {
      key = src + '|' + cfg.w;
    }
    if (!cache.has(key)) cache.set(key, enqueue(() => (cfg.fit ? measure(src, cfg.w) : analyse(src, cfg.w))));
    cache.get(key)
      .then((r) => {
        if (!r) return;
        if (r.url && !cfg.fit) {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = r.url;
          img.classList.add('lb-cut');
        } else if (r.transparent) {
          img.classList.add('lb-cut');
        }
        s.info = r.info;
        if (!cfg.cutOnly && r.info) {
          applyFit(img, r.info, cfg.pad, cfg.zmax, cfg.zmin);
          ro.observe(img);
        }
      })
      .catch(() => {})
      .finally(() => reveal(img));
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        io.unobserve(e.target);
        const s = state.get(e.target);
        if (s) processImg(e.target, s.cfg);
      }
    },
    { rootMargin: '600px 400px' }
  );

  function register(img, cfg) {
    if (state.has(img)) return;
    if (!cfg.cutOnly && img.closest(SKIP)) return;
    if (cfg.fit && img.closest('.dialog-zoomed-gallery, zoom-dialog')) return; // zoom view shows the full original
    if (img.classList.contains('deferred-media__poster-image')) return;
    state.set(img, { cfg, started: false, info: null });
    img.classList.add('lb-await');
    setTimeout(() => reveal(img), cfg.fit ? 1500 : 4000); // never leave an image hidden
    io.observe(img);
  }

  function scan(scope = document) {
    for (const t of TARGETS) scope.querySelectorAll(t.sel).forEach((img) => register(img, t));
  }

  let queued = false;
  const queue = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      scan();
    }, 120);
  };

  const start = () => {
    scan();
    new MutationObserver((list) => {
      if (list.some((m) => m.addedNodes.length)) queue();
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
